import { Box, Stack } from '@mui/material'
import { FC, useMemo } from 'react'
import EngagementDetailsIcon from '../../../../assets/images/engagement-details-icon.svg?react'
import {
  Activity,
  EscalateToIntrusionWithDescriptionOutdoorActivity,
  EscalateToIntrusionWithDescriptionOutdoorActivityType,
  OutdoorEngagement
} from '../../../../api/models'
import { EngagementEscalatedWithDescriptionOutdoorActivityResultType } from '../../../../api/generated/museum-v1-types'
import { ActivityFieldBox } from './ActivityCard/ActivityFieldBox'
import { camelCaseToSplitWords, governingSopDisplayName } from '../activityUtils'
import { EqualColumnsGrid } from './EqualColumnsGrid'
import { ActivityTimeValue } from './ActivityCard/ActivityTimeValue'

interface EngagementDetailsProps {
  engagement: OutdoorEngagement
  /**
   * Can also be ProcessedActivity, but we only need the properties that are on Activity for this component
   */
  activities: Activity[]
}

export const EngagementDetails: FC<EngagementDetailsProps> = ({ engagement, activities }) => {
  const category = engagement.result?.resolution?.category
  const escalationActivity = activities.find(
    activity =>
      activity.type === EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription &&
      activity.completed?.result?.type === EngagementEscalatedWithDescriptionOutdoorActivityResultType.EngagementEscalatedWithDescription
  ) as EscalateToIntrusionWithDescriptionOutdoorActivity | undefined
  const escalationDetails =
    escalationActivity?.completed?.result &&
    'escalationDetails' in escalationActivity.completed.result &&
    escalationActivity.completed.result.type === EngagementEscalatedWithDescriptionOutdoorActivityResultType.EngagementEscalatedWithDescription
      ? escalationActivity.completed.result.escalationDetails
      : undefined

  const durationInMs = useMemo(() => {
    const firstActivity = activities[0]
    const lastActivity = activities.at(-1)
    if (!firstActivity || !lastActivity) {
      return 0
    }

    const startTimestamp = engagement.createdTimestamp || firstActivity.started?.timestamp || firstActivity.created.timestamp
    const endTimestamp = lastActivity?.completed?.timestamp || ''
    const totalDuration = new Date(endTimestamp).getTime() - new Date(startTimestamp).getTime()

    if (Number.isNaN(totalDuration) || totalDuration < 0) {
      return 0
    }

    return Math.floor(totalDuration)
  }, [activities, engagement.createdTimestamp])

  return (
    <Stack direction="column" sx={{ padding: 'var(--spacing-s)', gap: 'var(--spacing-2xs)' }} data-testid="side-panel-details">
      <Stack direction="row" sx={{ alignItems: 'center', gap: 'var(--spacing-s)', paddingBottom: 'var(--spacing-s)' }}>
        <EngagementDetailsIcon />
        <Box sx={{ fontWeight: '700' }}>Engagement Details</Box>
      </Stack>

      <EqualColumnsGrid>
        <ActivityFieldBox label="Category">
          <Box data-testid={`engagement-details-category-${category}`}>{camelCaseToSplitWords(category)}</Box>
        </ActivityFieldBox>
        <ActivityFieldBox label="Duration">
          <ActivityTimeValue
            ms={durationInMs}
            seconds={durationInMs / 1000}
            data-testid="engagement-details-duration"
            tooltipProps={{
              placement: 'bottom-start',
              arrow: true,
              componentsProps: {
                arrow: {
                  sx: {
                    // Using `bottom-start` causes the tooltip arrow to be aligned to the right edge
                    // and it looks bad since the thing that it's pointing to is on the left. This is needed to
                    // move the arrow back to the center of the tooltip.
                    left: '50% !important',
                    transform: 'translateX(-50%) !important'
                  }
                }
              }
            }}
          />
        </ActivityFieldBox>
        <ActivityFieldBox label="2WA SOP">
          {/* No recorded configuration (CLVMON-637 not yet shipped, or a legacy engagement) defaults to
              Standard — the Current SOP is the only one that existed before this field, not a guess. */}
          <Box data-testid="engagement-details-sop">{governingSopDisplayName(engagement.configuration?.isTwoWayAudioEscalationOnly)}</Box>
        </ActivityFieldBox>
      </EqualColumnsGrid>

      {escalationDetails?.description && (
        <ActivityFieldBox label="Escalation Description">
          <Box data-testid={`engagement-details-escalation-description`}>{escalationDetails.description}</Box>
        </ActivityFieldBox>
      )}
    </Stack>
  )
}
